/* eslint-disable @typescript-eslint/no-unused-vars */
import { base } from '../base';
import { Theme } from '../theme';
import { createFontFace } from '../base/font-face';
import InterRegularWoff from '../Inter/Inter-Regular.woff';
import InterRegularWoff2 from '../Inter/Inter-Regular.woff2';
import InterMediumWoff from '../Inter/Inter-Medium.woff';
import InterMediumWoff2 from '../Inter/Inter-Medium.woff2';
import InterSemiBoldWoff from '../Inter/Inter-SemiBold.woff';
import InterSemiBoldWoff2 from '../Inter/Inter-SemiBold.woff2';
import InterBoldWoff from '../Inter/Inter-Bold.woff';
import InterBoldWoff2 from '../Inter/Inter-Bold.woff2';

import { primitiveColors } from '../colors';
import { primitiveSizes } from '../sizes';
import { buildComponentSizes } from '../component-sizes';
import { semanticSizes } from '../semantic-sizes';
import { componentColors } from './component-colors';
import { semanticColors } from './semantic-colors';
import { semanticTypography } from './semantic-typography';
import { fontCode } from '../font-code';

export const fontFace = createFontFace({
    weights: [
        { weight: 400, woff: InterRegularWoff, woff2: InterRegularWoff2 },
        { weight: 500, woff: InterMediumWoff, woff2: InterMediumWoff2 },
        { weight: 600, woff: InterSemiBoldWoff, woff2: InterSemiBoldWoff2 },
        { weight: 700, woff: InterBoldWoff, woff2: InterBoldWoff2 },
    ],
});

const defaBeige = '#ECE2D0';

const black = primitiveColors.gray['000'];
const white = primitiveColors.gray.fff;

const colorPrimary = '#3B3B40';
const colorSecondary = primitiveColors.gray[300];

export const defa: Theme = {
    ...base,
    name: 'Defa',

    ...semanticColors,
    ...semanticSizes,
    componentColors,
    componentSizes: buildComponentSizes(semanticTypography),
    colorPrimary,
    colorSecondary,
    modalBackgroundColor: primitiveColors.gray[800],
    firstSideBackgroundColor: primitiveColors.gray[200],
    secondSideBackgroundColor: white,

    errorColor: primitiveColors.red[500],
    activeColor: primitiveColors.defaBlue[500],
    focusColor: black,
    successColor: primitiveColors.green[400],
    disabledColor: primitiveColors.gray[700],

    // Typography
    typography: semanticTypography,
    fontBase: 'Inter, sans-serif',
    fontCode,
    textColor: primitiveColors.gray[500],
    headerColor: white,
    textSize: '14px',
    lineHeight: '20px',
    descriptionSize: '14px',
    descriptionColor: primitiveColors.gray[800],
    descriptionLineHeight: '18px',
    fontFace,
    linkTextColor: primitiveColors.defaBlue[500],

    // Button
    buttonColorPrimary: colorPrimary,
    buttonTextColorPrimary: white,
    buttonColorSecondary: colorSecondary,
    buttonTextColorSecondary: black,
    buttonTextColorDisabled: primitiveColors.gray[800],
    buttonColorDisabled: primitiveColors.gray[700],
    buttonColorTertiary: white,
    buttonBorderTertiary: '1px solid #00000029',

    // Card
    cardBorder: 'none',
    cardBorderActive: 'none',
    cardBorderPrint: `1px solid ${primitiveColors.gray[300]}`,
    cardBackground: white,
    cardBorderRadius: `${primitiveSizes.radius.standard}px`,
    cardPadding: base.spacingRaw(4),
    cardBackgroundActive: 'linear-gradient(136.45deg, #4361EE 0%, #52BDD5 100%)',
    cardTextActive: '#fff',
    cardSupportingTextColor: primitiveColors.gray[750],

    // Selectable Grid Item
    selectableBorderColor: primitiveColors.gray[300],
    selectableBorderColorFocus: primitiveColors.gray[300],
    selectableBorderColorActive: colorPrimary,

    // accordion
    accordionBorder: `1px solid ${primitiveColors.gray[300]}`,
    accordionBorderActive: `2px solid ${colorPrimary}`,

    // Input
    inputBackground: colorPrimary,
    inputActiveBackground: colorPrimary,
    inputReadOnlyBackground: primitiveColors.gray[300],
    inputBorder: '2px solid transparent',
    inputBorderActive: '2px solid transparent',
    inputBorderReadOnly: `2px solid ${primitiveColors.gray[200]}`,
    inputBorderColor: colorSecondary,
    inputActiveColor: colorPrimary,
    inputLabelColor: primitiveColors.gray[500],
    inputInputColor: white,
    inputMessageBorder: '2px solid',
    inputMessageColor: white,
    inputMessageColorInfo: primitiveColors.defaBlue[500],
    inputMessageColorError: primitiveColors.red[500],

    listHeaderColor: primitiveColors.gray[750],
    tableHeaderColor: primitiveColors.gray[800],

    // Simple Table
    simpleTableBorderColor: primitiveColors.gray[200],
    simpleTableHeaderColor: black,
    simpleTableBodyColor: primitiveColors.gray[800],

    actionLinkTextColor: primitiveColors.defaBlue[500],

    checkedAvatarColor: primitiveColors.green[400],
    avatarSecondaryColor: primitiveColors.gray[700],

    // Menu
    menuBackgroundColor: white,
    menuDividerColor: primitiveColors.gray[100],

    // Side menu
    sideMenuIconColor: black,
    sideMenuAlternativeBackground: primitiveColors.gray[100],

    // Group
    groupDivider: primitiveColors.gray[200],

    // Chip
    chipColor: primitiveColors.gray[200],

    // Progressindicator
    progressDonutRingColor: primitiveColors.gray[300],
    progressDonutIndicatorColor1: colorPrimary,
    progressDonutIndicatorColor2: '#52BDD4',
    progressSpinnerColor: primitiveColors.gray[800],

    // RadioList
    radioListBackgroundColors: [white, defaBeige, primitiveColors.defaDarkBlue[500]],
    radioListTextColors: [black, black, white],

    // Tooltip
    tooltipBackground: black,
    tooltipBorder: 'none',
    tooltipBorderRadius: `${primitiveSizes.radius.standard}px`,
    tooltipColor: white,
    tooltipShadow: '0px 2px 10px 0px #00000012',

    // Toast
    toastBackground: primitiveColors.gray[800],
    toastBorder: `1px solid ${primitiveColors.gray[200]}`,
    toastBorderRadius: `${primitiveSizes.radius.standard}px`,
    toastTitleColor: white,
    toastColor: white,
    toastShadow: '0px 2px 10px 0px #00000012',
};
